/**
 * Created by neo on 24.04.22.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { Audited, createAuditedFields } from '../../Audited';
import { ProgramParams, createProgramParams } from '../Program/ProgramParams';
import { MindfulnessProgramTemplateDayUnit } from './Template/MindfulnessProgramTemplateDayUnit';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { UnitSchedule, createUnitSchedule } from '../Program/UnitSchedule';
import { ActivityLog, newActivityLog } from '../../Activity/ActivityLog';

export type MindfulnessProgramWeekUnitJson = {
  id: string;
  programId: string;
  phaseId: string;
  weekId: string;
  dayTemplateId: string;
  unitTemplateId: string;
  activityLogId?: string;
  activityLogDateTime?: string;
  params: ProgramParams;
  skippedAt?: string;
  isLastUnit: boolean;
};

export type MindfulnessProgramWeekUnit = Audited & {
  programId: string;
  phaseId: string;
  weekId: string;
  dayTemplateId: string;
  unitTemplateId: string;
  activityLogId?: string;
  activityLogDateTime?: Date;
  params: ProgramParams;
  skippedAt?: Date;
  schedule?: UnitSchedule;
  isLastUnit: boolean;
  unitTemplate?: MindfulnessProgramTemplateDayUnit;
  activityLog?: ActivityLog;
  readonly skipped: boolean;
  update(other: MindfulnessProgramWeekUnit): void;
  skip(): Promise<void>;
  complete(activityLog: ActivityLog): Promise<MindfulnessProgramWeekUnit>;
  createActivityLog(startDate: Date, endDate: Date, data?: Record<string, number>): Promise<ActivityLog>;
  scheduleLater(newSchedule: UnitSchedule): Promise<MindfulnessProgramWeekUnit>;
};

export function createMindfulnessProgramWeekUnit(json?: Partial<MindfulnessProgramWeekUnit>): MindfulnessProgramWeekUnit {
  const base = createAuditedFields(json);
  const unit: MindfulnessProgramWeekUnit = makeAutoObservable({
    ...base,
    programId: json?.programId ?? '',
    phaseId: json?.phaseId ?? '',
    weekId: json?.weekId ?? '',
    dayTemplateId: json?.dayTemplateId ?? '',
    unitTemplateId: json?.unitTemplateId ?? '',
    activityLogId: json?.activityLogId,
    activityLogDateTime: json?.activityLogDateTime ? new Date(json.activityLogDateTime as any) : undefined,
    params: createProgramParams(json?.params),
    skippedAt: json?.skippedAt ? new Date(json.skippedAt as any) : undefined,
    schedule: json?.schedule ? createUnitSchedule(json.schedule) : undefined,
    isLastUnit: json?.isLastUnit ?? false,
    unitTemplate: undefined as MindfulnessProgramTemplateDayUnit | undefined,
    activityLog: undefined as ActivityLog | undefined,

    get skipped(): boolean {
      return !!this.skippedAt;
    },

    update(other: MindfulnessProgramWeekUnit): void {
      this.programId = other.programId;
      this.phaseId = other.phaseId;
      this.weekId = other.weekId;
      this.dayTemplateId = other.dayTemplateId;
      this.unitTemplateId = other.unitTemplateId;
      this.activityLogId = other.activityLogId;
      this.activityLogDateTime = other.activityLogDateTime;
      this.params = createProgramParams(other.params);
      this.skippedAt = other.skippedAt;
      this.schedule = other.schedule ? createUnitSchedule(other.schedule) : undefined;
      this.isLastUnit = other.isLastUnit;
    },

    skip(): Promise<void> {
      return HttpBackend.post(`/coach-program/mindfulness/${this.programId}/week/${this.weekId}/unit/${this.id}/skip`).then(() => {
        runInAction(() => {
          this.skippedAt = new Date();
        });
      });
    },

    complete(activityLog: ActivityLog): Promise<MindfulnessProgramWeekUnit> {
      if (!this.activityLogId) {
        this.activityLogId = activityLog.id;
        this.activityLog = activityLog;

        return HttpBackend.post(`/coach-program/mindfulness/${this.programId}/week/${this.weekId}/unit/${this.id}/complete`, {
          activityLogId: activityLog.id,
          dateTime: activityLog.startDate,
        }).then(() => this);
      }
      return Promise.resolve(this);
    },

    async createActivityLog(startDate: Date, endDate: Date, data: Record<string, number> = {}): Promise<ActivityLog> {
      if (this.activityLog) {
        return this.activityLog;
      }

      if (!this.unitTemplate) {
        throw new Error('Unit template not loaded. Use useMindfulnessUnitTemplate hook first.');
      }

      const template = this.unitTemplate;

      if (!data.DURATION) {
        data.DURATION = Math.round((endDate.valueOf() - startDate.valueOf()) / 1000);
      }

      return newActivityLog({
        type: template.activityIdentifier,
        source: {
          source: 'kinastic',
          sourceId: this.id,
          sourceType: 'mindfulnessUnit',
        },
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        data,
        linkedData: {
          programId: this.programId,
          weekId: this.weekId,
          unitId: this.id,
          dayTemplateId: this.dayTemplateId,
        },
      });
    },

    scheduleLater(newSchedule: UnitSchedule): Promise<MindfulnessProgramWeekUnit> {
      this.schedule = newSchedule.copy();
      return HttpBackend.post(
        `/coach-program/mindfulness/${this.programId}/week/${this.weekId}/unit/${this.id}/schedule`,
        newSchedule,
      ).then(() => this);
    },
  });

  return unit;
}

export const MindfulnessProgramWeekUnitApi = {
  findOne(unitId: string): Promise<MindfulnessProgramWeekUnit> {
    return HttpBackend.get(`/coach-program/mindfulness/unit/${unitId}`).then((res) => createMindfulnessProgramWeekUnit(res));
  },
};
